import { axiosPrivate } from "@/lib/axios/axios-private";
import { ApiError } from "@/lib/api/errors";
import type {
  ListVideoHistoryParams,
  VideoHistoryItem,
  VideoHistoryListData,
  VideoHistoryListEnvelope,
  VideoHistoryListResult,
  VideoHistoryMasterOption,
  VideoHistoryMastersEnvelope,
} from "@/types/video-history-types";

function parseHistoryPayload(
  payload: VideoHistoryListData | undefined,
  fallbackPage: number,
  fallbackPageSize: number,
): VideoHistoryListResult {
  if (!payload) {
    return {
      items: [],
      page: fallbackPage,
      pageSize: fallbackPageSize,
      total: 0,
    };
  }

  return {
    items: (payload.items ?? []).map((item) => {
      const record = item as VideoHistoryItem & {
        video_url?: string | null;
        thumbnail_url?: string | null;
      };
      return {
        ...record,
        videoUrl: record.videoUrl ?? record.video_url ?? null,
        thumbnailUrl: record.thumbnailUrl ?? record.thumbnail_url ?? null,
      };
    }),
    page: payload.page ?? fallbackPage,
    pageSize: payload.pageSize ?? fallbackPageSize,
    total: payload.total ?? 0,
  };
}

export async function fetchVideoHistory(
  params: ListVideoHistoryParams = {},
): Promise<VideoHistoryListResult> {
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;

  const { data: envelope } = await axiosPrivate.get<VideoHistoryListEnvelope>(
    "/api/videos/history",
    {
      params: {
        page,
        pageSize,
        ...(params.search ? { search: params.search } : {}),
        ...(params.status ? { status: params.status } : {}),
        ...(params.sortBy ? { sortBy: params.sortBy } : {}),
        ...(params.sortOrder ? { sortOrder: params.sortOrder } : {}),
        ...(params.dateFrom ? { dateFrom: params.dateFrom } : {}),
        ...(params.dateTo ? { dateTo: params.dateTo } : {}),
        ...(params.masterId ? { masterId: params.masterId } : {}),
      },
    },
  );

  if (!envelope.success || envelope.code !== 200) {
    throw new ApiError(
      envelope.errorMessage || "Failed to fetch video history",
      envelope.code,
    );
  }

  return parseHistoryPayload(envelope.data, page, pageSize);
}

export async function fetchHistoryMasterOptions(): Promise<
  VideoHistoryMasterOption[]
> {
  const { data: envelope } =
    await axiosPrivate.get<VideoHistoryMastersEnvelope>(
      "/api/videos/history/masters",
    );

  if (!envelope.success || envelope.code !== 200) {
    throw new ApiError(
      envelope.errorMessage || "Failed to fetch master",
      envelope.code,
    );
  }

  return envelope.data?.items ?? [];
}

export function getVideoHistoryTotalPages(
  result: VideoHistoryListResult,
): number {
  if (result.total > 0 && result.pageSize > 0) {
    return Math.max(1, Math.ceil(result.total / result.pageSize));
  }

  return Math.max(1, result.page);
}

export type { VideoHistoryItem };
